import { useApi, useLoad } from "@allergy123/api-client/react";
import { Button, Card, Status } from "@allergy123/ui";
import { useState } from "react";
import { Link, useParams } from "react-router";
import { classPath, enrollmentFor, isComplete, minutesLabel, sectionOf, stageLabel } from "../lib";

/** One module: its classes in order, the knowledge check that follows, and the enroll/start action. */
export function ModulePage() {
  const { section, module } = useParams();
  const api = useApi();
  const [busy, setBusy] = useState(false);
  const data = useLoad(async (a) => {
    const [cat, learning] = await Promise.all([a.catalog(), a.learning.me()]);
    return { cat, learning };
  });
  if (data.state === "error") return <Status kind="error" label={`Couldn't load this module (${data.error?.message})`} />;
  if (!data.data) return <p className="text-ink-secondary">Loading…</p>;
  const { cat, learning } = data.data;
  const m = cat.modules.find((x) => x.sectionId === section && x.slug === module);
  if (!m) return <Status kind="error" label="This module isn't in the catalog." />;
  const s = sectionOf(cat, m.sectionId);
  const e = enrollmentFor(learning.enrollments, m.code);
  const done = m.classes.filter((k) => isComplete(e, k.code)).length;

  async function enroll() {
    setBusy(true);
    try {
      await api.learning.enroll(m!.code);
      data.reload();
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <p className="mb-2 text-small text-ink-secondary">
        <Link to="/training">Training</Link> · {s?.title}
      </p>
      <h1 className="mb-1 text-h1 font-semibold text-anchor">
        {m.code} · {m.title}
      </h1>
      <p className="mb-8 text-small text-ink-secondary">
        {m.classes.length} class{m.classes.length === 1 ? "" : "es"} · {stageLabel[m.releaseStage] ?? m.releaseStage} · release {m.releaseVersion}
      </p>

      {!e ? (
        <Card tone="tinted" className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="max-w-[60ch]">You're not enrolled in this module. Enrolling records the release you start, so your completions stay tied to the exact content you saw.</p>
          <Button onClick={() => void enroll()} disabled={busy}>
            {busy ? "Enrolling…" : "Enroll"}
          </Button>
        </Card>
      ) : (
        <p className="mb-8 text-ink-secondary">
          {done} of {m.classes.length} classes complete.
        </p>
      )}

      <ol className="grid gap-3">
        {m.classes.map((k) => {
          const complete = isComplete(e, k.code);
          const hasMedia = k.media.includes("video");
          return (
            <Card key={k.code} as="li" dense className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-small font-medium text-ink-secondary">
                  {k.code} · {minutesLabel(k.estimatedMinutes)}
                </p>
                <h2 className="text-h3 font-semibold text-anchor">{k.title}</h2>
                {!hasMedia && <p className="text-small text-ink-secondary">Outline only — video in production.</p>}
              </div>
              <div className="flex items-center gap-4">
                {complete && <Status kind="success" label="Complete" />}
                <Button variant="secondary" surface="card" href={classPath(k.code)} disabled={!e}>
                  {complete ? "Review" : "Open"}
                </Button>
              </div>
            </Card>
          );
        })}
      </ol>

      {m.classes.some((k) => k.checkCode) && (
        <section className="mt-10">
          <h2 className="mb-3 text-h2 font-semibold text-anchor">Knowledge check</h2>
          {m.classes
            .filter((k) => k.checkCode)
            .map((k) => {
              const results = e?.knowledgeResults.filter((r) => r.checkCode === k.checkCode) ?? [];
              const passed = results.some((r) => r.passed);
              return (
                <Card key={k.checkCode} dense className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p>After {k.code}: a short, low-stakes check with remediation for anything missed.</p>
                    {results.length > 0 && (
                      <p className="text-small text-ink-secondary">
                        {results.length} attempt{results.length === 1 ? "" : "s"} · best {Math.max(...results.map((r) => r.scorePercent))}%
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-4">
                    {passed && <Status kind="success" label="Passed" />}
                    <Button variant="secondary" surface="card" href={`/training/checks/${k.checkCode}`} disabled={!e}>
                      {passed ? "Review" : "Take the check"}
                    </Button>
                  </div>
                </Card>
              );
            })}
        </section>
      )}
    </>
  );
}
